import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import {
  signinAsync,
  verifyTwoFactorAsync,
  resetTwoFactor,
  selectMessage,
  selectStatus,
  selectRequiresTwoFactor,
  selectTwoFactorEmail,
} from './signInSlice';
import { useTheme } from '../../useTheme';

const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';
const CODE_LENGTH = 6;
const PRIMARY_COLOR = '#043c58';

const OtpInput = ({ onComplete, hasError, onReset }) => {
    const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
    const digitsRef = useRef(Array(CODE_LENGTH).fill(''));
    const inputRefs = useRef([]);

    useEffect(() => {
        if (onReset) {
            const empty = Array(CODE_LENGTH).fill('');
            digitsRef.current = empty;
            setDigits(empty);
            inputRefs.current[0]?.focus();
        }
    }, [onReset]);

    const handleChange = (value, index) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        if (!digit && value !== '') return;

        const newDigits = [...digitsRef.current];
        newDigits[index] = digit;
        digitsRef.current = newDigits;
        setDigits([...newDigits]);

        const fullCode = newDigits.join('');
        const filled = newDigits.filter(Boolean).length;

        if (digit && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        if (filled === CODE_LENGTH) {
            onComplete(fullCode);
        } else {
            onComplete('');
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            const newDigits = [...digitsRef.current];
            if (newDigits[index]) {
                newDigits[index] = '';
                digitsRef.current = newDigits;
                setDigits([...newDigits]);
                onComplete('');
            } else if (index > 0) {
                newDigits[index - 1] = '';
                digitsRef.current = newDigits;
                setDigits([...newDigits]);
                inputRefs.current[index - 1]?.focus();
                onComplete('');
            }
            e.preventDefault();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const getBorder = (isFocused) => {
        if (hasError) return '2px solid #e24c4c';
        if (isFocused) return `2px solid ${PRIMARY_COLOR}`;
        return '1px solid #ced4da';
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            {digits.map((digit, index) => (
                <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    style={{
                        width: '48px',
                        height: '56px',
                        textAlign: 'center',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        border: getBorder(false),
                        outline: 'none',
                        color: '#1a1a1a',
                        background: '#fff',
                        transition: 'border 0.15s',
                        caretColor: PRIMARY_COLOR,
                    }}
                    onFocus={(e) => { e.target.style.border = getBorder(true); }}
                    onBlur={(e) => { e.target.style.border = getBorder(false); }}
                />
            ))}
        </div>
    );
};

const maskEmail = (email) => {
    if (!email) return '';
    const [user, domain] = email.split('@');
    if (!domain) return email;
    const visible = Math.min(3, Math.floor(user.length / 2));
    const masked = user.slice(0, visible) + '*'.repeat(user.length - visible);
    return `${masked}@${domain}`;
};

const SignIn = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [captchaToken, setCaptchaToken] = useState(null);
    const [code, setCode] = useState('');
    const [otpResetKey, setOtpResetKey] = useState(0);
    const widgetIdRef = useRef(null);

    // Instância própria (não recebe via prop — SignIn é montado pelo Route
    // dentro de Auth.js, sem repasse de props customizadas) — lê o mesmo
    // localStorage, então já nasce com o valor certo; só não reagiria a uma
    // troca de tema feita em outro lugar enquanto essa tela está montada,
    // o que não acontece hoje (não há esse controle na tela de login).
    const { layoutColorMode } = useTheme();

    const dispatch = useDispatch();
    const message = useSelector(selectMessage);
    const status = useSelector(selectStatus);
    const requiresTwoFactor = useSelector(selectRequiresTwoFactor);
    const twoFactorEmail = useSelector(selectTwoFactorEmail);
    const isLoading = status === 'loading';

    useEffect(() => {
        const renderWidget = () => {
            if (window.grecaptcha && widgetIdRef.current === null) {
                widgetIdRef.current = window.grecaptcha.render(RECAPTCHA_CONTAINER_ID, {
                    sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
                    theme: layoutColorMode === 'dark' ? 'dark' : 'light',
                    callback: (token) => setCaptchaToken(token),
                    'expired-callback': () => setCaptchaToken(null),
                    'error-callback': () => setCaptchaToken(null),
                });
            }
        };

        if (!requiresTwoFactor) {
            if (window.grecaptcha && window.grecaptcha.render) {
                renderWidget();
            } else {
                window.onRecaptchaLoad = renderWidget;
            }
        }

        return () => { widgetIdRef.current = null; };
        // layoutColorMode só é lido na primeira renderização do widget (o
        // grecaptcha não permite trocar o tema de um widget já montado sem
        // destruir e recriar) — não precisa re-executar se ele mudar depois.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requiresTwoFactor]);

    const handleLogin = () => {
        dispatch(signinAsync({ email, password, captchaToken }));
        if (window.grecaptcha && widgetIdRef.current !== null) {
            window.grecaptcha.reset(widgetIdRef.current);
        }
        setCaptchaToken(null);
    };

    const handleVerifyCode = () => {
        dispatch(verifyTwoFactorAsync({ email: twoFactorEmail, code }));
    };

    const handleBack = () => {
        dispatch(resetTwoFactor());
        setCode('');
        setOtpResetKey((k) => k + 1);
    };

    if (requiresTwoFactor) {
        return (
            <div className='px-24 py-4 md:px-6 lg:px-8 flex align-items-center justify-content-center'>
                <div className="surface-card p-8 shadow-2 border-round w-full lg:w-6">
                    <div className="text-center mb-5">
                        <img src="assets/layout/images/AE-Logo_AZUL.png"   alt="logo" height="70" className="mb-3 ae-logo-light" />
                        <img src="assets/layout/images/AE-Logo_BRANCO.png" alt="logo" height="70" className="mb-3 ae-logo-dark" />
                        <i className="pi pi-envelope mb-3" style={{ fontSize: '3rem', color: PRIMARY_COLOR, display: 'block' }} />
                        <div className="text-900 text-2xl font-medium mb-2">Verificação em duas etapas</div>
                        <p className="text-600 m-0">Um código de 6 dígitos foi enviado para:</p>
                        <p className="font-medium text-900 mt-1">{maskEmail(twoFactorEmail)}</p>
                    </div>

                    <div>
                        <OtpInput
                            onComplete={(val) => setCode(val)}
                            hasError={!!message}
                            onReset={otpResetKey}
                        />

                        <div className="mt-3 mb-4" style={{ minHeight: '20px' }}>
                            {message && <small className='text-pink-500'>{message}</small>}
                        </div>

                        <Button
                            label="Verificar"
                            icon="pi pi-check"
                            className="w-full mb-3"
                            disabled={code.length !== CODE_LENGTH || isLoading}
                            loading={isLoading}
                            onClick={handleVerifyCode}
                        />
                        <Button
                            label="Voltar"
                            icon="pi pi-arrow-left"
                            className="w-full p-button-text"
                            onClick={handleBack}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='px-24 py-4 md:px-6 lg:px-8 flex align-items-center justify-content-center'>
            <div className="surface-card p-8 shadow-2 border-round w-full lg:w-6">
                <div className="text-center mb-5">
                    <img src="assets/layout/images/AE-Logo_AZUL.png"   alt="logo" height="70" className="mb-3 ae-logo-light" />
                    <img src="assets/layout/images/AE-Logo_BRANCO.png" alt="logo" height="70" className="mb-3 ae-logo-dark" />
                    <div className="text-900 text-2xl font-medium mb-3">Login</div>
                </div>

                <div>
                    <label htmlFor="email1" className="block text-900 font-medium mb-2">Email</label>
                    <InputText id="email1" type="text" className="w-full mb-3" value={email} onChange={(e) => setEmail(e.target.value)} />

                    <label htmlFor="password1" className="block text-900 font-medium mb-2">Senha</label>
                    <InputText id="password1" type="password" className="w-full mb-3" value={password} onChange={(e) => setPassword(e.target.value)} />

                    <div className="flex align-items-center justify-content-between mb-4">
                        <label className='text-pink-500'>{message}</label>
                    </div>

                    <div style={{ display: isLoading ? 'none' : 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                        <div id={RECAPTCHA_CONTAINER_ID}></div>
                    </div>

                    <Button label="Entrar" icon="pi pi-user" className="w-full" disabled={!captchaToken || isLoading} loading={isLoading} onClick={handleLogin} />
                </div>
            </div>
        </div>
    );
};

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(SignIn, comparisonFn);